import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { of, ReplaySubject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { FacilityDobryAgentService } from '../../services/facility-dobry-agent.service';
import { GooglePlaceDobryAgent } from '../../interface/facility-dobry-agent';
import { ListResponse } from '../../interface/response/list-response';

@Component({
  selector: 'app-google-place-dobry-agent-select',
  templateUrl: './google-place-dobry-agent-select.component.html',
  styleUrls: ['./google-place-dobry-agent-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GooglePlaceDobryAgentSelectComponent),
      multi: true,
    },
  ],
})
export class GooglePlaceDobryAgentSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Wyszukaj wizytówkę Google';
  @Input() placeholder = 'Wpisz nazwę placówki lub adres';
  @Input() required = false;
  @Input() limit = 10;

  @Output() placeSelected = new EventEmitter<GooglePlaceDobryAgent | null>();

  searchControl = new FormControl('');

  items: GooglePlaceDobryAgent[] = [];
  selectedPlace: GooglePlaceDobryAgent | null = null;

  value: string | null = null;
  disabled = false;
  loading = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private facilityDobryAgentService: FacilityDobryAgentService,
  ) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        map((value: string | null) => (value ?? '').trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((query: string) => {
          if (query.length < 2) {
            this.items = [];
            return of({ items: [] } as ListResponse<GooglePlaceDobryAgent>);
          }

          this.loading = true;

          return this.facilityDobryAgentService.searchGooglePlace(query, this.limit)
            .pipe(
              catchError(() => of({ items: [] } as ListResponse<GooglePlaceDobryAgent>)),
              finalize(() => {
                this.loading = false;
              }),
            );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((response: ListResponse<GooglePlaceDobryAgent>) => {
        this.items = response.items ?? [];
      });
  }

  writeValue(value: string | null): void {
    this.value = value ?? null;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.searchControl.disable({ emitEvent: false });
    } else {
      this.searchControl.enable({ emitEvent: false });
    }
  }

  changeValue(placeId: string | null): void {
    this.value = placeId;

    if (!placeId) {
      this.selectedPlace = null;
      this.onChange(null);
      this.onTouched();
      this.placeSelected.emit(null);
      return;
    }

    const place = this.items.find((item) => item.place_id === placeId) ?? null;

    this.selectedPlace = place;
    this.onChange(placeId);
    this.onTouched();
    this.placeSelected.emit(place);
  }

  getSelectedLabel(): string {
    if (this.selectedPlace) {
      return this.selectedPlace.description;
    }

    return this.value ?? '';
  }

  compareByPlaceId(a: string | GooglePlaceDobryAgent, b: string | GooglePlaceDobryAgent): boolean {
    const first = typeof a === 'object' ? a?.place_id : a;
    const second = typeof b === 'object' ? b?.place_id : b;

    return first === second;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
